import { Fragment, useState } from 'react';
import useSWR from 'swr';
import type { LeaderboardCircuit, LeaderboardEntry } from '@kangaroo/shared';
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Collapse,
  IconButton,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import EmojiEventsOutlinedIcon from '@mui/icons-material/EmojiEventsOutlined';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { PageHeader, SectionCard, colors } from '@kangaroo/ui';

import { fetchLeaderboard, leaderboardKey } from '../../features/leaderboard';
import { setupSnapshotRows } from '../../lib/kartSetupHistory';
import { formatLapTime } from '../../lib/lapFormat';

function formatEntryDate(recordedAtMs: number | null) {
  if (recordedAtMs === null) {
    return 'Date unknown';
  }

  return new Date(recordedAtMs).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

function formatGapMs(gapMs: number) {
  return `+${(gapMs / 1000).toFixed(3)}`;
}

function rankColor(rank: number, isDark: boolean) {
  switch (rank) {
    case 1:
      return '#CA8A04';
    case 2:
      return isDark ? '#CBD5E1' : '#64748B';
    case 3:
      return '#B45309';
    default:
      return isDark ? colors.primary[200] : colors.primary[600];
  }
}

function LeaderboardEntryRow({
  entry,
  rank,
  bestLapMs,
  expanded,
  onToggle,
}: {
  entry: LeaderboardEntry;
  rank: number;
  bestLapMs: number;
  expanded: boolean;
  onToggle: () => void;
}) {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const gapMs = entry.lapTimeMs - bestLapMs;
  const hasSetup = entry.setup !== null;
  const setupRows = entry.setup ? setupSnapshotRows(entry.setup) : [];
  const badgeColor = rankColor(rank, isDark);

  return (
    <Fragment>
      <Stack
        direction="row"
        spacing={1.25}
        onClick={hasSetup ? onToggle : undefined}
        sx={{
          alignItems: 'center',
          px: 1.5,
          py: 1,
          borderRadius: '10px',
          border: '1px solid',
          borderColor: 'divider',
          bgcolor:
            rank === 1
              ? isDark
                ? 'rgba(250,204,21,0.06)'
                : 'rgba(250,204,21,0.1)'
              : isDark
                ? 'rgba(255,255,255,0.02)'
                : '#F9FAFB',
          cursor: hasSetup ? 'pointer' : 'default',
          '&:hover': {
            bgcolor: isDark ? 'rgba(255,255,255,0.05)' : '#F3F4F6',
          },
        }}
      >
        <Box
          sx={{
            width: 30,
            height: 30,
            borderRadius: '50%',
            display: 'grid',
            placeItems: 'center',
            bgcolor: isDark ? 'rgba(2,10,18,0.6)' : '#FFFFFF',
            border: `2px solid ${badgeColor}`,
            color: badgeColor,
            fontSize: '0.78rem',
            fontWeight: 900,
            flexShrink: 0,
          }}
        >
          {rank}
        </Box>

        <Box sx={{ minWidth: 96, flexShrink: 0 }}>
          <Typography sx={{ fontSize: '1.02rem', fontWeight: 900, fontVariantNumeric: 'tabular-nums', lineHeight: 1.1 }}>
            {formatLapTime(entry.lapTimeMs)}
          </Typography>
          <Typography sx={{ fontSize: '0.68rem', color: 'text.secondary', fontWeight: 700 }}>
            {rank === 1 ? 'Personal best' : formatGapMs(gapMs)}
          </Typography>
        </Box>

        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography sx={{ fontSize: '0.8rem', fontWeight: 750, color: 'text.primary' }} noWrap>
            {formatEntryDate(entry.recordedAtMs)}
            {entry.lapNumber !== null ? ` · L${entry.lapNumber}` : ''}
          </Typography>
          <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary' }} noWrap>
            {[entry.kartLabel, entry.driverName].filter(Boolean).join(' · ') || 'Kart unknown'}
          </Typography>
        </Box>

        {hasSetup ? (
          <IconButton size="small" color="default" aria-label={expanded ? 'Hide setup' : 'Show setup'}>
            {expanded ? <KeyboardArrowUpIcon fontSize="small" /> : <KeyboardArrowDownIcon fontSize="small" />}
          </IconButton>
        ) : (
          <Chip label="No setup" size="small" variant="outlined" sx={{ fontSize: '0.64rem', height: 20 }} />
        )}
      </Stack>

      {hasSetup ? (
        <Collapse in={expanded} unmountOnExit>
          <Box
            sx={{
              mx: 1.5,
              mt: 0.5,
              mb: 0.75,
              px: 1.5,
              py: 1.25,
              borderRadius: '10px',
              border: '1px dashed',
              borderColor: 'divider',
              bgcolor: isDark ? 'rgba(2,10,18,0.4)' : '#F9FAFB',
            }}
          >
            <Typography sx={{ fontSize: '0.66rem', color: 'text.secondary', fontWeight: 850, letterSpacing: '0.06em', mb: 0.75 }}>
              KART SETUP {entry.setupCapturedAt ? `· LOGGED ${formatEntryDate(Date.parse(entry.setupCapturedAt))}` : '· CURRENT (NOT DATED TO SESSION)'}
            </Typography>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(4, 1fr)' },
                gap: 1,
              }}
            >
              {setupRows.map(([label, value]) => (
                <Box key={label}>
                  <Typography sx={{ fontSize: '0.64rem', color: 'text.secondary', fontWeight: 750 }}>{label}</Typography>
                  <Typography sx={{ fontSize: '0.8rem', color: 'text.primary', fontWeight: 800 }}>{value}</Typography>
                </Box>
              ))}
            </Box>
          </Box>
        </Collapse>
      ) : null}
    </Fragment>
  );
}

function CircuitLeaderboardCard({ circuit }: { circuit: LeaderboardCircuit }) {
  const [expandedKey, setExpandedKey] = useState<string | null>(null);

  return (
    <SectionCard
      title={circuit.circuitName}
      subtitle={`${circuit.sessionCount} session${circuit.sessionCount === 1 ? '' : 's'} · best ${formatLapTime(circuit.bestLapMs) ?? '—'}`}
      action={<EmojiEventsOutlinedIcon sx={{ color: 'warning.main' }} />}
    >
      <Stack spacing={0.75}>
        {circuit.entries.map((entry, index) => (
          <LeaderboardEntryRow
            key={entry.sessionKey}
            entry={entry}
            rank={index + 1}
            bestLapMs={circuit.bestLapMs}
            expanded={expandedKey === entry.sessionKey}
            onToggle={() =>
              setExpandedKey((current) => (current === entry.sessionKey ? null : entry.sessionKey))
            }
          />
        ))}
      </Stack>
    </SectionCard>
  );
}

export function LeaderboardPage() {
  const { data, error, isLoading } = useSWR(leaderboardKey, fetchLeaderboard, {
    revalidateOnFocus: false,
  });

  return (
    <Box>
      <PageHeader
        title="Leaderboard"
        subtitle="Your top times at each circuit and the kart setup that day."
        icon={<EmojiEventsOutlinedIcon />}
      />

      {isLoading ? (
        <Box sx={{ display: 'grid', placeItems: 'center', minHeight: 220 }}>
          <CircularProgress size={28} />
        </Box>
      ) : null}

      {error ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          Could not load the leaderboard. Please try again.
        </Alert>
      ) : null}

      {!isLoading && !error && (data?.length ?? 0) === 0 ? (
        <Alert severity="info">
          No lap data yet. Upload MyChron sessions and your best times will show up here per circuit.
        </Alert>
      ) : null}

      <Stack spacing={2}>
        {data?.map((circuit) => (
          <CircuitLeaderboardCard key={circuit.circuitKey} circuit={circuit} />
        ))}
      </Stack>
    </Box>
  );
}
